import type { ComponentType } from 'react';
import { CSSProperties, memo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ViewIds } from '@ardoq/api-types';
import { connect, dispatchAction } from '@ardoq/rxbeach';
import { combineLatest, distinctUntilChanged, map } from 'rxjs';
import { FeatureContext, featureOperations, Features } from '@ardoq/features';
import { componentGraphSelectionChanged } from '@ardoq/component-selection';
import { shallowEqual } from 'utils/objectUtils';
import { featureContext$ } from 'streams/featureContext$';
import { MAIN_PANE_LEFT } from 'streams/views/mainContent/types';
import { ViewContainer } from 'tabview/ViewContainer';
import { ViewSidePanel } from 'views/mainApp/viewSidePanel/ViewSidePanel';
import { RightPane } from 'views/mainApp/rightPane/rightPane';
import { ComponentViewpointFab } from 'views/mainApp/fab/componentFab/ComponentViewpointFab';
import { ContextToolBar } from 'canvas/contextToolBar/ContextToolBar';
import { isDiscoverJourney$ } from 'canvas/isDiscoverJourney';
import { ConnectedViewPaneHeader as ViewPaneHeader } from './ardoqStudio/viewPaneHeader/ViewPaneHeader';
import { DetailsDrawer } from './ardoqStudio/detailsDrawer/DetailsDrawer';
import { ResizableSidebarEditorAsOverlay } from './ResizableSidebarEditorAsOverlay';
import { leftPaneCreated } from './actions';
import { ViewPaneContainerElement } from './viewPaneContainerAtoms';
import { MainViewChrome } from './MainViewChrome';
import { ErrorBoundary } from '@ardoq/error-boundary';
import { DisabledViewReplacement } from 'tabview/DisabledViewReplacement';
import { VisualizationsErrorMessage } from 'tabview/VisualizationErrorMessage';
import { logVisualizationError } from 'tabview/utils';
import { ViewpointEmptyState } from './ardoqStudio/emptyState/ViewpointEmptyState';
import { getViewpointModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/viewpointModeViewSettings$';
import { toViewpointModeMainViewInstanceId } from './mainViewInstanceIds';
import { MainViewContent } from './types';
import { ViewpointModeTab, viewpointModeTab$ } from './viewpointModeTab$';
import { ModernizedBlockDiagramView } from 'tabview/blockDiagram/ModernizedBlockDiagramView';
import { ViewpointModeDependencyMapView } from 'tabview/dependencyMap/viewpointMode/ViewpointModeDependencyMapView';
import { ViewpointModeRelationshipsView } from 'tabview/relationsD3View/hierarchical/ViewpointModeRelationshipsView';
import { ViewpointModeTimelineView } from 'tabview/timeline2024/ViewpointModeTimelineView';
import { ViewpointModeBubbleChart } from 'tabview/bubbleChart/ViewpointModeBubbleChart';
import { ConnectedComponentMatrix2 as ComponentMatrix2View } from 'tabview/componentMatrix/componentMatrix2/ComponentMatrix2View';
import { ConnectedBlocksView as BlocksView } from 'tabview/blocks/view/ConnectedBlocksView';
import { ViewpointModeAssistantDiagram } from 'tabview/assistantDiagram/ViewpointModeAssistantDiagram';
import { ViewpointModeAssistantBlocks } from 'tabview/assistantBlocks/ViewpointModeAssistantBlocks';
import { FlexibleBlockDiagramView } from 'tabview/flexibleBlockDiagram/FlexibleBlockDiagramView';
import { ConnectedHierarchicalTreemapViewpointView as HierarchicalTreemapViewpointView } from 'tabview/hierarchicalTreemap/viewpoint/HierarchicalTreemapViewpointView';
import { ConnectedHierarchicalPackingViewpointView as HierarchicalPackingViewpointView } from 'tabview/hierarchicalTreemap/viewpoint/HierarchicalPackingViewpointView';
import { ConnectedSunburstViewpointView as SunburstViewpointView } from 'tabview/hierarchicalTreemap/viewpoint/SunburstViewpointView';
import { Surface3DViewController } from 'tabview/xyzCharts/Surface3DViewController';
import { ContourViewController } from 'tabview/xyzCharts/ContourViewController';
import {
  Bubble3DViewController,
  Scatter3DViewController,
} from 'tabview/xyzCharts/Points3DViewController';
import { Network3DViewController } from 'tabview/network3d/Network3DViewController';
import { SpiderViewController } from 'tabview/radialCharts/spider/SpiderViewController';
import { PolarViewController } from 'tabview/radialCharts/polar/PolarViewController';
import type { ViewpointModeViewKey } from 'views/environmentals/viewSettings/types';
import type { ViewpointModeViewProps } from 'tabview/types';
import { tourAnchorIds } from '@ardoq/product-tours';
import { NoodleSearchBox } from 'dynamicReferenceCreation/NoodleSearchBox';
import { ConnectedViewAiBar } from './viewAiBar/ViewAiBar';

const VIEWPOINT_MODE_VIEW_COMPONENTS: {
  [ViewId in ViewpointModeViewKey]: ComponentType<
    ViewpointModeViewProps<ViewId>
  >;
} = {
  [ViewIds.MODERNIZED_BLOCK_DIAGRAM]: ModernizedBlockDiagramView,
  [ViewIds.DEPENDENCY_MAP_2]: ViewpointModeDependencyMapView,
  [ViewIds.RELATIONSHIPS_3]: ViewpointModeRelationshipsView,
  [ViewIds.TIMELINE]: ViewpointModeTimelineView,
  [ViewIds.BUBBLE]: ViewpointModeBubbleChart,
  [ViewIds.COMPONENT_MATRIX_2]: ComponentMatrix2View,
  [ViewIds.BLOCKS]: BlocksView,
  [ViewIds.ASSISTANT_DIAGRAM]: ViewpointModeAssistantDiagram,
  [ViewIds.ASSISTANT_BLOCKS]: ViewpointModeAssistantBlocks,
  [ViewIds.FLEXIBLE_BLOCK_DIAGRAM]: FlexibleBlockDiagramView,
  [ViewIds.HIERARCHICAL_TREEMAP]: HierarchicalTreemapViewpointView,
  [ViewIds.HIERARCHICAL_PACKING]: HierarchicalPackingViewpointView,
  [ViewIds.SUNBURST]: SunburstViewpointView,
  [ViewIds.SURFACE_3D]: Surface3DViewController,
  [ViewIds.CONTOUR]: ContourViewController,
  [ViewIds.SCATTER_3D]: Scatter3DViewController,
  [ViewIds.BUBBLE_3D]: Bubble3DViewController,
  [ViewIds.NETWORK_3D]: Network3DViewController,
  [ViewIds.SPIDER_2]: SpiderViewController,
  [ViewIds.POLAR]: PolarViewController,
};

const ViewpointModeContainer = styled(ViewPaneContainerElement)`
  right: 0;
  overflow-x: hidden;
`;

const ScrollContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: min-content 1fr;
  height: 100%;
  overflow: auto;
  width: 100%;
`;

const widthOfSidePanelHeaderButtons = 400;

const VIEW_IDS_WITHOUT_SELECTION_HANDLING = new Set<ViewIds>([
  ViewIds.MODERNIZED_BLOCK_DIAGRAM,
  ViewIds.RELATIONSHIPS_3,
  ViewIds.BLOCK_DIAGRAM,
  ViewIds.BLOCKS,
  ViewIds.FLEXIBLE_BLOCK_DIAGRAM,
]);

// Temporary workaround to support selection in the views for the canvas prototype.
const selectionHandler = ({ target }: MouseEvent) => {
  if (!(target instanceof HTMLElement || target instanceof SVGElement)) {
    return;
  }
  const ele = target.closest<HTMLElement | SVGElement>('[data-component-id');
  const id = ele?.dataset.componentId;
  dispatchAction(
    componentGraphSelectionChanged({
      graphSelection: id ? [id] : [],
    })
  );
};

type ViewpointModeMainViewContent = MainViewContent<
  ViewpointModeViewProps<ViewpointModeViewKey>
>;

type ViewpointModeMainViewProps<ViewId extends ViewpointModeViewKey> = {
  activeTabId: ViewpointModeViewKey | ViewIds.NONE;
  content: MainViewContent<ViewpointModeViewProps<ViewId>>;
};

const resolveActiveView = ({
  activeTabId,
  metaInfo,
}: ViewpointModeTab): ViewpointModeMainViewContent => {
  if (activeTabId === ViewIds.NONE) {
    return { kind: 'none' };
  }
  if (!metaInfo) {
    return { kind: 'disabled' };
  }
  const viewInstanceId = toViewpointModeMainViewInstanceId(activeTabId);
  return {
    kind: 'view',
    viewProps: {
      viewId: activeTabId,
      viewInstanceId,
      isViewpointMode: true,
      viewSettingsWithChanges$: getViewpointModeViewSettingsWithChanges$(
        activeTabId,
        viewInstanceId
      ),
    },
  };
};

const viewpointModeMainViewProps$ = viewpointModeTab$.pipe(
  map(viewPaneTab => ({
    activeTabId: viewPaneTab.activeTabId,
    content: resolveActiveView(viewPaneTab),
  }))
);

const ViewpointModeMainView = memo(
  <ViewId extends ViewpointModeViewKey>({
    activeTabId,
    content,
  }: ViewpointModeMainViewProps<ViewId>) => {
    const ViewComponent: ComponentType<ViewpointModeViewProps<ViewId>> | null =
      content.kind === 'view'
        ? VIEWPOINT_MODE_VIEW_COMPONENTS[content.viewProps.viewId]
        : null;
    return (
      <>
        <MainViewChrome />
        {content.kind === 'view' && <ViewpointEmptyState />}

        <ErrorBoundary
          logError={() =>
            logVisualizationError({
              viewId: activeTabId,
              location: MAIN_PANE_LEFT,
              isViewpointMode: true,
            })
          }
          errorMessage={<VisualizationsErrorMessage />}
          resetKeys={[activeTabId]}
        >
          {content.kind === 'view' && ViewComponent && (
            <ViewComponent
              key={content.viewProps.viewInstanceId}
              {...content.viewProps}
            />
          )}
          {content.kind === 'disabled' && <DisabledViewReplacement />}
        </ErrorBoundary>
      </>
    );
  }
);

export const ConnectedViewpointModeMainView = connect(
  ViewpointModeMainView,
  viewpointModeMainViewProps$
);

type ViewpointModeMainViewPaneStreamedProps = {
  hasCanvasSelectionHandling: boolean;
  showOldCanvasPrototype: boolean;
  hasNoodleWithSearch: boolean;
};

const toViewpointModeMainViewPaneProps = (
  featureContext: FeatureContext,
  isDiscoverJourney: boolean,
  { activeTabId }: ViewpointModeTab
): ViewpointModeMainViewPaneStreamedProps => {
  const hasCanvasPrototype = featureOperations.hasFeature(
    featureContext,
    Features.CANVAS_PROTOTYPE
  );
  return {
    hasCanvasSelectionHandling:
      activeTabId !== ViewIds.NONE &&
      (hasCanvasPrototype || isDiscoverJourney) &&
      !VIEW_IDS_WITHOUT_SELECTION_HANDLING.has(activeTabId),
    // KFQ12026
    // TODO CL: Remove this when switching to the new toolbar
    showOldCanvasPrototype: isDiscoverJourney && !hasCanvasPrototype,
    hasNoodleWithSearch: featureOperations.hasFeature(
      featureContext,
      Features.REFERENCE_CREATION_NOODLE_WITH_SEARCH
    ),
  };
};

const viewpointModeMainViewPaneProps$ = combineLatest([
  featureContext$,
  isDiscoverJourney$,
  viewpointModeTab$,
]).pipe(
  map(args => toViewpointModeMainViewPaneProps(...args)),
  distinctUntilChanged<ViewpointModeMainViewPaneStreamedProps>(shallowEqual)
);

type ViewpointModeMainViewPaneOwnProps = {
  style: CSSProperties;
  isRightEditorPaneOpen: boolean;
  isDetailsDrawerOpen: boolean;
};

type ViewpointModeMainViewPaneProps = ViewpointModeMainViewPaneOwnProps &
  ViewpointModeMainViewPaneStreamedProps;

const ViewpointModeMainViewPane = memo(
  ({
    style,
    isRightEditorPaneOpen,
    isDetailsDrawerOpen,
    hasCanvasSelectionHandling,
    showOldCanvasPrototype,
    hasNoodleWithSearch,
  }: ViewpointModeMainViewPaneProps) => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
      dispatchAction(leftPaneCreated(ref));
      return () => dispatchAction(leftPaneCreated(null));
    }, []);

    useEffect(() => {
      if (!hasCanvasSelectionHandling || !ref.current) {
        return;
      }
      const container = ref.current;
      container.addEventListener('click', selectionHandler, { capture: true });
      return () =>
        container.removeEventListener('click', selectionHandler, {
          capture: true,
        });
    }, [hasCanvasSelectionHandling]);

    return (
      <ViewpointModeContainer style={style} data-test-id={MAIN_PANE_LEFT}>
        <ScrollContainer>
          <div>
            <ViewPaneHeader />
            <ConnectedViewAiBar />
          </div>
          <ViewContainer ref={ref} data-tour-id={tourAnchorIds.canvasGraph}>
            {hasNoodleWithSearch && <NoodleSearchBox />}
            <ConnectedViewpointModeMainView />
          </ViewContainer>
          <ViewSidePanel />
          <ResizableSidebarEditorAsOverlay
            initialWidth={widthOfSidePanelHeaderButtons}
            minWidth={60}
            isRightEditorPaneOpen={isRightEditorPaneOpen}
          >
            <RightPane isViewpointMode />
          </ResizableSidebarEditorAsOverlay>
          {isDetailsDrawerOpen && <DetailsDrawer />}
          <ComponentViewpointFab />
        </ScrollContainer>
        {showOldCanvasPrototype && <ContextToolBar />}
      </ViewpointModeContainer>
    );
  }
);

export const ConnectedViewpointModeMainViewPane = connect<
  ViewpointModeMainViewPaneProps,
  ViewpointModeMainViewPaneStreamedProps
>(ViewpointModeMainViewPane, viewpointModeMainViewPaneProps$);
